(() => {
  const root = document.querySelector('[data-pci-catalog]');
  if (!root) return;

  const filters = root.querySelector('[data-pci-filters]');
  const openBtn = root.querySelector('[data-pci-filters-open]');
  const closeBtns = root.querySelectorAll('[data-pci-filters-close]');
  const sortForm = root.querySelector('[data-pci-sort]');
  const sortMenu = sortForm?.querySelector('.pci-catalog__sort-menu');
  const sortTrigger = sortForm?.querySelector('[data-pci-sort-trigger]');
  const sortList = sortForm?.querySelector('[data-pci-sort-list]');
  const sortValue = sortForm?.querySelector('[data-pci-sort-value]');
  const sortCurrent = sortForm?.querySelector('[data-pci-sort-current]');
  const priceRoot = root.querySelector('[data-pci-price]');
  const typeButtons = [...root.querySelectorAll('[data-pci-type]')];
  const items = [...root.querySelectorAll('[data-pci-item]')];
  const emptyState = root.querySelector('[data-pci-empty]');
  const clearButtons = [...root.querySelectorAll('[data-pci-clear]')];
  const pagination = root.querySelector('[data-pci-pagination]');
  const applyBtn = root.querySelector('[data-pci-price-apply]');

  const state = {
    type: '',
    low: null,
    high: null,
  };

  const setFiltersOpen = (open) => {
    if (!filters || !openBtn) return;
    filters.classList.toggle('is-open', open);
    openBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
    document.documentElement.classList.toggle('pci-catalog-filters-open', open);
  };

  const setSortOpen = (open) => {
    if (!sortMenu || !sortTrigger || !sortList) return;
    sortMenu.classList.toggle('is-open', open);
    sortTrigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    sortList.hidden = !open;
  };

  const formatPrice = (value, symbol) => {
    const amount = Number(value);
    const formatted = Number.isFinite(amount)
      ? amount.toLocaleString(undefined, { maximumFractionDigits: 0 })
      : value;
    return `${symbol || ''}${formatted}`;
  };

  const syncClearVisibility = () => {
    const floor = Number(priceRoot?.dataset.floor || 0);
    const ceiling = Number(priceRoot?.dataset.ceiling || 0);
    const priceActive = state.low > floor || state.high < ceiling;
    const active = Boolean(state.type) || priceActive;
    clearButtons.forEach((btn) => {
      btn.hidden = !active;
    });
    if (pagination) pagination.hidden = active;
  };

  const applyFilters = () => {
    let visible = 0;
    items.forEach((item) => {
      const type = item.getAttribute('data-type') || '';
      const price = Number(item.getAttribute('data-price'));
      const typeOk = !state.type || type.toLowerCase() === state.type.toLowerCase();
      const priceOk =
        (!Number.isFinite(state.low) || price >= state.low) &&
        (!Number.isFinite(state.high) || price <= state.high);
      const show = typeOk && priceOk;
      item.hidden = !show;
      if (show) visible += 1;
    });

    if (emptyState) emptyState.hidden = visible !== 0 || items.length === 0;
    syncClearVisibility();
    setFiltersOpen(false);
  };

  const setType = (type) => {
    state.type = type || '';
    typeButtons.forEach((btn) => {
      const active = (btn.getAttribute('data-pci-type') || '') === state.type;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    applyFilters();
  };

  const initPriceSlider = () => {
    if (!priceRoot) return null;

    const floor = Number(priceRoot.dataset.floor || 0);
    const ceiling = Number(priceRoot.dataset.ceiling || 0);
    const step = Number(priceRoot.dataset.step || 1) || 1;
    const symbol = priceRoot.dataset.currency || '';
    const dual = priceRoot.querySelector('[data-pci-dual]');
    const minThumb = priceRoot.querySelector('[data-pci-thumb="min"]');
    const maxThumb = priceRoot.querySelector('[data-pci-thumb="max"]');
    const minInput = priceRoot.querySelector('[data-pci-price-min]');
    const maxInput = priceRoot.querySelector('[data-pci-price-max]');
    const minLabel = priceRoot.querySelector('[data-pci-price-min-label]');
    const maxLabel = priceRoot.querySelector('[data-pci-price-max-label]');

    if (!dual || !minThumb || !maxThumb || !minInput || !maxInput || ceiling <= floor) return null;

    let low = Number(minInput.value);
    let high = Number(maxInput.value);
    if (!Number.isFinite(low)) low = floor;
    if (!Number.isFinite(high)) high = ceiling;

    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
    const snap = (value) => Math.round(value / step) * step;
    const span = () => ceiling - floor || 1;

    const render = () => {
      const lowPct = ((low - floor) / span()) * 100;
      const highPct = ((high - floor) / span()) * 100;
      dual.style.setProperty('--pci-price-low', `${lowPct}%`);
      dual.style.setProperty('--pci-price-high', `${highPct}%`);
      minInput.value = String(low);
      maxInput.value = String(high);
      if (minLabel) minLabel.textContent = formatPrice(low, symbol);
      if (maxLabel) maxLabel.textContent = formatPrice(high, symbol);
      minThumb.setAttribute('aria-valuenow', String(low));
      maxThumb.setAttribute('aria-valuenow', String(high));
      minThumb.setAttribute('aria-valuetext', formatPrice(low, symbol));
      maxThumb.setAttribute('aria-valuetext', formatPrice(high, symbol));
      minThumb.setAttribute('aria-valuemax', String(high));
      maxThumb.setAttribute('aria-valuemin', String(low));
    };

    const valueFromClientX = (clientX) => {
      const rail = dual.querySelector('[data-pci-dual-rail]') || dual;
      const rect = rail.getBoundingClientRect();
      const ratio = rect.width ? (clientX - rect.left) / rect.width : 0;
      return snap(floor + clamp(ratio, 0, 1) * span());
    };

    const bindThumb = (thumb, which) => {
      const onPointerMove = (event) => {
        const next = valueFromClientX(event.clientX);
        if (which === 'min') low = clamp(next, floor, high);
        else high = clamp(next, low, ceiling);
        render();
      };

      const onPointerUp = () => {
        thumb.classList.remove('is-dragging');
        thumb.releasePointerCapture?.(thumb._pciPointerId);
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
        window.removeEventListener('pointercancel', onPointerUp);
      };

      thumb.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        thumb._pciPointerId = event.pointerId;
        thumb.classList.add('is-dragging');
        thumb.setPointerCapture?.(event.pointerId);
        onPointerMove(event);
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp);
        window.addEventListener('pointercancel', onPointerUp);
      });

      thumb.addEventListener('keydown', (event) => {
        const delta =
          event.key === 'ArrowRight' || event.key === 'ArrowUp'
            ? step
            : event.key === 'ArrowLeft' || event.key === 'ArrowDown'
              ? -step
              : event.key === 'Home'
                ? which === 'min'
                  ? floor - low
                  : low - high
                : event.key === 'End'
                  ? which === 'min'
                    ? high - low
                    : ceiling - high
                  : 0;
        if (!delta && event.key !== 'Home' && event.key !== 'End') return;
        event.preventDefault();
        if (which === 'min') low = clamp(low + delta, floor, high);
        else high = clamp(high + delta, low, ceiling);
        render();
      });
    };

    bindThumb(minThumb, 'min');
    bindThumb(maxThumb, 'max');
    render();

    return {
      getRange: () => ({ low, high, floor, ceiling }),
      reset: () => {
        low = floor;
        high = ceiling;
        render();
      },
    };
  };

  const priceApi = initPriceSlider();

  const applyPrice = () => {
    if (!priceApi) return;
    const { low, high } = priceApi.getRange();
    state.low = low;
    state.high = high;
    applyFilters();
  };

  const clearFilters = () => {
    state.type = '';
    typeButtons.forEach((btn) => {
      const active = (btn.getAttribute('data-pci-type') || '') === '';
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    priceApi?.reset();
    if (priceApi) {
      const { floor, ceiling } = priceApi.getRange();
      state.low = floor;
      state.high = ceiling;
    } else {
      state.low = null;
      state.high = null;
    }
    applyFilters();
  };

  typeButtons.forEach((btn) => {
    btn.addEventListener('click', () => setType(btn.getAttribute('data-pci-type') || ''));
  });

  applyBtn?.addEventListener('click', applyPrice);
  clearButtons.forEach((btn) => btn.addEventListener('click', clearFilters));

  openBtn?.addEventListener('click', () => setFiltersOpen(true));
  closeBtns.forEach((btn) => btn.addEventListener('click', () => setFiltersOpen(false)));

  sortTrigger?.addEventListener('click', (event) => {
    event.stopPropagation();
    setSortOpen(sortTrigger.getAttribute('aria-expanded') !== 'true');
  });

  sortForm?.querySelectorAll('[data-pci-sort-option]').forEach((option) => {
    option.addEventListener('click', () => {
      const value = option.getAttribute('data-value');
      if (!value || !sortValue || !sortForm) return;
      if (sortValue.value === value) {
        setSortOpen(false);
        return;
      }
      sortValue.value = value;
      if (sortCurrent) sortCurrent.textContent = option.textContent.trim();
      setSortOpen(false);
      sortForm.requestSubmit ? sortForm.requestSubmit() : sortForm.submit();
    });
  });

  document.addEventListener('click', (event) => {
    if (sortMenu && !sortMenu.contains(event.target)) setSortOpen(false);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      setFiltersOpen(false);
      setSortOpen(false);
    }
  });

  // Initial price bounds = full range so everything shows.
  if (priceApi) {
    const { floor, ceiling } = priceApi.getRange();
    state.low = floor;
    state.high = ceiling;
  }
  syncClearVisibility();
})();
